import { Journey } from '@audibene/ta.utils-opportunity'
import { addYears } from 'date-fns'
import { ComponentProps, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Navigate, useNavigate } from 'react-router'
import { Country, EventType, Route, RouteParameters, ToastSubType } from '../../../constants'
import {
  useEventTracking,
  useFeatureFlags,
  useFormAnalytics,
  useIsSchedulingV2RedirectPending,
  useNotification,
  useSearchParameters,
  useTimeZones,
  useTrackError,
} from '../../../hooks'
import { useAppointmentsService } from '../../../hooks/services'
import { useCustomerProfile } from '../../../hooks/useCustomerProfile'
import { useCustomerProfileService } from '../../../hooks/services/customerProfileService'
import { useReservations } from '../../../hooks/useReservations'
import { ReservationCreateRequest, ReservationStatus } from '../../../types'
import { Address, CustomerProfile, Relation, RelationType } from '../../../types/customerProfileService'
import {
  codeToCountry,
  createTimeZonesFromGroups,
  hydratePathname,
  isSlotUnavailableError,
  logFactory,
  selectRandomItem,
} from '../../../util'
import { getEarliestBusinessDate } from '../../../util/skipBusinessDays'
import { handleSubmissionFailure } from '../../Appointment/util'
import { Form } from '../../forms'
import { Issues } from '../../Issues'
import { QueryRefreshTimer } from '../../schedule/New/QueryRefreshTimer'
import { Loading } from '../Common/Loading'
import { ReservationForm } from './Form'
import { getDefaultReservationFormValues, ReservationFormData, getReservationFormValidationSchema } from './Form/constants'
import { differenceInCalendarDays, differenceInBusinessDays } from 'date-fns'
import { EventData } from '../../../types/event'
import { stateMismatchModalText } from './constants/stateMismatchModalText'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'

const log = logFactory('NewReservationForm')

const isValidAddress = (address?: Address) => {
  const country = codeToCountry(address?.country)

  return country === Country.US ? Boolean(address?.state && address?.country) : Boolean(address?.country)
}

const customerProfileAddress = (customerProfile?: CustomerProfile): Address | undefined => {
  if (isValidAddress(customerProfile?.shippingAddress)) {
    return customerProfile?.shippingAddress
  }
  if (isValidAddress(customerProfile?.billingAddress)) {
    return customerProfile?.billingAddress
  }
  return undefined
}

const getCustomerProfileLeadRelation = (leadGid: string, customerProfile?: CustomerProfile): Relation | undefined => {
  const leadRelation = customerProfile?.relations?.find((relation) => relation.gid === leadGid && relation.type === RelationType.LEAD)
  return leadRelation
}

/**
 * Initial page to create a reservation
 */
const New = () => {
  const { createReservation } = useAppointmentsService()
  const { getCustomerProfile } = useCustomerProfileService()
  const navigate = useNavigate()
  const isV2RedirectPending = useIsSchedulingV2RedirectPending()
  const searchParameters = useSearchParameters()
  const [isReservationEnabledFeatureFlag, shouldSkipTwoBusinessDaysFeatureFlag, isAvailabilityCacheEnabled] = useFeatureFlags<boolean>([
    'ENABLE_RESERVATION_WITH_PROVIDER_RECOMMENDATION',
    'SKIP_48_BUSINESS_HOURS_ON_RESERVATIONS',
    'ENABLE_AVAILABILITY_CACHE',
  ])
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const trackError = useTrackError()
  const { track: trackEvent } = useEventTracking()
  const { readSummary } = useFormAnalytics<ReservationFormData>()
  const [now] = useState(new Date())
  const [formKey, setFormKey] = useState(0)

  // State for managing state mismatch modal
  const [isStateMismatchModalOpen, setIsStateMismatchModalOpen] = useState(false)
  const [stateMismatchInfo, setStateMismatchInfo] = useState<{
    oldState: string | undefined
    newState: string | undefined
  }>({ oldState: undefined, newState: undefined })

  // Query parameters
  const leadGid = searchParameters.lead_gid
  const leadTimezone = searchParameters.time_zone
  const journey = searchParameters.journey as Journey | undefined
  const insuranceCompany = searchParameters.insurance_company
  const restrictToShopSalesforceId = searchParameters.restrict_to_shop
  if (Array.isArray(restrictToShopSalesforceId)) {
    throw new Error('restrict_to_shop search parameter should not be an array')
  }

  const { timeZones = {}, isLoading: areTimeZonesLoading } = useTimeZones()

  const ianaTimezones = createTimeZonesFromGroups(timeZones)
  const { customerProfile, isLoading: isCustomerProfileLoading } = useCustomerProfile()
  const { data: existingReservations, isLoading: areReservationsLoading } = useReservations(
    { expires_at: [now, addYears(now, 1)], lead_gid: [leadGid as string], status: [ReservationStatus.CONFIRMED] },
    Boolean(leadGid)
  )
  const address = customerProfileAddress(customerProfile)

  const customerCountry = useMemo(() => {
    return codeToCountry(customerProfile?.country)
  }, [customerProfile?.country])

  const handleStateMismatchModalClose = async () => {
    if (stateMismatchInfo.newState) {
      log(`🔄 Refreshing recommendations for updated state: ${stateMismatchInfo.newState}`)

      try {
        // The await must precede setFormKey: after the refetch resolves, New re-renders
        // with the fresh customerProfile, recomputing defaultReservationFormValues.state
        // before the Form remounts and reads defaultValues.
        await queryClient.invalidateQueries(['useCustomerProfile'], undefined, { throwOnError: true })
        setFormKey((k) => k + 1)
        setIsStateMismatchModalOpen(false)
        notify({
          message: stateMismatchModalText.toastMessage(stateMismatchInfo.newState),
          type: ToastSubType.SUCCESS,
        })
      } catch (error) {
        trackError(error instanceof Error ? error : String(error))
        notify({
          message: 'Could not refresh the provider list — please try again.',
          type: ToastSubType.UNHANDLED_ERROR,
        })
        return
      }
    } else {
      setIsStateMismatchModalOpen(false)
    }

    setStateMismatchInfo({ oldState: undefined, newState: undefined })
  }

  if (!isReservationEnabledFeatureFlag) {
    return <Issues summary={{ 'reservation.disabled': [] }} />
  }

  const issuesSummary: ComponentProps<typeof Issues>['summary'] = {}

  if (!journey) {
    issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.journey']
  }

  if (!leadGid) {
    issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.lead_gid']
  }

  if (!leadTimezone) {
    issuesSummary['url.parameters'] = [...(issuesSummary['url.parameters'] ?? []), 'searchParam.time_zone']
  }

  if (Object.keys(issuesSummary).length) {
    return <Issues summary={issuesSummary} />
  }

  const isLoading = isCustomerProfileLoading || areTimeZonesLoading || areReservationsLoading
  if (isLoading) {
    return <Loading />
  }

  if (existingReservations?.length && !isV2RedirectPending) {
    const pathname = hydratePathname(Route.RESERVATION_SUCCESS, {
      [RouteParameters.COUNTRY]: existingReservations[0].country,
      [RouteParameters.RESERVATION_ID]: existingReservations[0].id,
    })
    return <Navigate to={pathname} replace />
  }

  const leadRelation = getCustomerProfileLeadRelation(leadGid as string, customerProfile)
  const availableTimezones = Object.keys(ianaTimezones) // Validation of timezone is here because in the future will come from CPS

  if (!leadRelation) {
    return (
      <Issues summary={{ 'lead.notFound': [] }}>
        <QueryRefreshTimer />
      </Issues>
    )
  }

  if (!address || !availableTimezones.includes(leadTimezone as string) || !customerProfile) {
    if (!address?.country) {
      issuesSummary['lead.fields'] = [...(issuesSummary['lead.fields'] ?? []), 'customer.address.country']
    }

    if (customerCountry === Country.US && !address?.state) {
      issuesSummary['lead.fields'] = [...(issuesSummary['lead.fields'] ?? []), 'customer.address.state']
    }

    if (!availableTimezones.includes(leadTimezone as string)) {
      issuesSummary['lead.fields'] = [...(issuesSummary['lead.fields'] ?? []), 'customer.timeZone']
    }

    return (
      <Issues summary={issuesSummary}>
        <QueryRefreshTimer />
      </Issues>
    )
  }

  // Create a reservation
  const onSubmit = async (data: ReservationFormData): Promise<void> => {
    try {
      log('🔄 Checking for customer state updates before reservation...')
      const latestCustomerProfile = await getCustomerProfile({
        anyRelationId: leadGid as string,
      })
      const latestAddress = customerProfileAddress(latestCustomerProfile)
      const latestState = latestAddress?.state

      const currentState = address?.state

      if (latestState !== currentState) {
        log(`🏠 Customer state mismatch detected: "${currentState}" → "${latestState}". Showing modal...`)

        setStateMismatchInfo({
          oldState: currentState,
          newState: latestState,
        })
        setIsStateMismatchModalOpen(true)
        return // Stop the submission process
      }

      log('✅ Customer state unchanged. Proceeding with reservation...')

      // Determine resource_ids based on combined booking
      let resource_ids: string[]

      if (data.isCombinedBooking && data.eligibleResourcePairs?.length) {
        // Combined booking: pick one [fitterId, cabinId] pair randomly for load balancing
        const selectedPair = selectRandomItem(data.eligibleResourcePairs)
        // Convert ResourcePair tuple to string array, filtering out undefined cabinId
        resource_ids = selectedPair.filter(Boolean) as string[]
        log('Creating combined booking with random selection', {
          selectedPair,
          resource_ids,
          eligibleCount: data.eligibleResourcePairs.length,
        })
      } else {
        // Single resource booking
        resource_ids = data.resourceIds
        log('Creating fitter-only booking', { resource_ids })
      }

      const reservationRequest: ReservationCreateRequest = {
        appointment_type: data.appointmentType,
        country: data.country,
        duration_in_minutes: data.durationInMinutes,
        insurance_company: insuranceCompany as string,
        journey: journey as string,
        lead_gid: data.leadGid,
        lead_salesforce_id: data.leadSalesforceId,
        recommendation_id: data.recommendationId,
        resource_ids,
        start_date: data.dateStartSelected as Date,
        timezone: data.timezone,
      }

      log('📩 Submitting reservation request...', reservationRequest)
      const reservation = await createReservation(reservationRequest)
      // Get form analytics data
      const { didError, errorCounts, totalClicks, totalSessionDurationInSeconds, submitCount } = readSummary()

      const eventData: EventData = {
        customerDefaultTimeZone: data.timezone,
        customerTimeZone: data.timezone,
        daysInTheFuture: differenceInCalendarDays(data.dateStartSelected as Date, now),
        daysInTheFutureBusiness: differenceInBusinessDays(data.dateStartSelected as Date, now),
        didError,
        durationDefaultInMinutes: data.durationInMinutes,
        durationInMinutes: data.durationInMinutes,
        errorCountResourceNotSelected: errorCounts['resourceIds'] ?? 0,
        errorCountStartDayNotSelected: errorCounts['dateStartSelected'] ?? 0,
        errorCountTimePickerInvalid: errorCounts['dateStartSelected'] ?? 0,
        formType: 'initial',
        isInitialValid: true,
        journey: journey as string,
        resourceIds: data.resourceIds,
        resourceTimeZone: data.timezone,
        startDate: (data.dateStartSelected as Date).toISOString(),
        startDay: (data.dateStartSelected as Date).getDate(),
        startDayOfWeek: (data.dateStartSelected as Date).getDay(),
        startTimeHour: Number(
          (data.dateStartSelected as Date).toLocaleTimeString('en', {
            timeZone: data.timezone,
            hour: 'numeric',
            hour12: false,
          })
        ),
        startTimeMinute: Number(
          (data.dateStartSelected as Date).toLocaleTimeString('en', {
            timeZone: data.timezone,
            minute: 'numeric',
            hour12: false,
          })
        ),
        submissionCount: submitCount + 1,
        totalClicks,
        totalSessionDurationInSeconds,
        type: data.appointmentType,
        country: customerProfile?.country,
        customerGid: customerProfile?.id?.toString(),
        opportunityGid: leadRelation?.objectId,
        shopGid: searchParameters.shop_gid as string,
      }

      trackEvent(EventType.RESERVATION_FORM_SUBMISSION_SUCCEEDED, {
        data: eventData,
      })

      if (!reservation?.id) {
        notify({
          message:
            'Sorry, the reservation might have been created but there was an error trying to redirect to the success page. Please refresh this page.',
          type: ToastSubType.UNHANDLED_ERROR,
        })
        return
      }
      const pathname = hydratePathname(Route.RESERVATION_SUCCESS, {
        [RouteParameters.COUNTRY]: reservation.country,
        [RouteParameters.RESERVATION_ID]: reservation.id,
      })
      navigate(pathname)
    } catch (error) {
      // Reset the form to default values after an unexpected submission error
      setFormKey((k) => k + 1)

      // Notify the user about the error
      const invalidateQueries = () => queryClient.invalidateQueries()
      const fallbackErrorMessage = 'Sorry, there was an error while creating a reservation.'
      trackEvent(EventType.RESERVATION_FORM_SUBMISSION_FAILED)

      handleSubmissionFailure({
        error: error,
        fallbackErrorMessage,
        invalidateQueries,
        notify,
        trackError,
        trackEvent,
      })

      if (isSlotUnavailableError(error)) {
        trackEvent(EventType.RESERVATION_FORM_SUBMISSION_FAILED_SLOT_UNAVAILABLE, {
          data: { wasAvailabilityCacheEnabled: isAvailabilityCacheEnabled },
        })
      }
    }
  }

  const defaultReservationFormValues = getDefaultReservationFormValues(customerCountry)

  defaultReservationFormValues.timezone = leadTimezone as string
  defaultReservationFormValues.leadGid = leadGid as string
  defaultReservationFormValues.leadSalesforceId = leadRelation.objectId

  if (customerCountry === Country.US) defaultReservationFormValues.state = address.state

  if (shouldSkipTwoBusinessDaysFeatureFlag) defaultReservationFormValues.dateStart = getEarliestBusinessDate()

  return (
    <>
      <Form<ReservationFormData>
        key={formKey}
        defaultValues={defaultReservationFormValues}
        onSubmit={onSubmit}
        validationSchema={getReservationFormValidationSchema(customerCountry)}
      >
        <ReservationForm
          ianaTimezones={ianaTimezones}
          customerProfile={customerProfile}
          journey={journey as Journey}
          insuranceCompany={insuranceCompany as string}
          restrictToShopSalesforceId={restrictToShopSalesforceId as string}
        />
      </Form>

      {/* State Mismatch Modal */}
      <Dialog
        open={isStateMismatchModalOpen}
        onClose={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{stateMismatchModalText.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {stateMismatchModalText.description}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{stateMismatchModalText.previousStateLabel}</strong> {stateMismatchInfo.oldState || stateMismatchModalText.notSpecified}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{stateMismatchModalText.currentStateLabel}</strong> {stateMismatchInfo.newState || stateMismatchModalText.notSpecified}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stateMismatchModalText.instruction}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStateMismatchModalClose} variant="contained" color="primary">
            {stateMismatchModalText.buttonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export { New as ReservationNew }
